
const CustomSolutions = () => {
  const customizationProcess = [
    {
      step: "1",
      title: "Requirements Analysis",
      description: "Detailed assessment of client-specific challenges, operating conditions, and performance requirements",
      icon: "ri-search-line"
    },
    {
      step: "2", 
      title: "Laboratory Development",
      description: "Custom formulation development and testing under simulated field conditions",
      icon: "ri-flask-line"
    },
    {
      step: "3",
      title: "Field Testing",
      description: "Pilot testing and optimization in actual field conditions with performance monitoring",
      icon: "ri-test-tube-line"
    },
    {
      step: "4",
      title: "Full Implementation",
      description: "Scale-up production and deployment with ongoing technical support and optimization",
      icon: "ri-rocket-line"
    }
  ];

  const customSolutionTypes = [
    {
      title: "Extreme Temperature Formulations",
      description: "Chemicals designed for high-temperature reservoirs and harsh operating conditions",
      applications: ["HTHP wells", "Steam injection", "Thermal recovery", "Geothermal operations"],
      image: "https://public.readdy.ai/ai/img_res/7beb03f273bbbe016ce37e687a431a35.jpg"
    },
    {
      title: "Environmentally Friendly Solutions",
      description: "Green chemistry alternatives that meet environmental regulations without compromising performance",
      applications: ["Offshore operations", "Sensitive areas", "Water treatment", "Biodegradable systems"],
      image: "https://readdy.ai/api/search-image?query=Green%20chemistry%20laboratory%20with%20Golden%20Black%20logo%2C%20environmentally%20friendly%20chemical%20development%20for%20sustainable%20oilfield%20operations&width=400&height=250&seq=green-chemistry&orientation=landscape"
    },
    {
      title: "Multi-Functional Additives",
      description: "Single products that address multiple challenges, reducing complexity and costs",
      applications: ["Combined inhibition", "Dual-purpose treatments", "Simplified logistics", "Cost optimization"],
      image: "https://readdy.ai/api/search-image?query=Multi-functional%20chemical%20additives%20with%20Golden%20Black%20branding%2C%20advanced%20formulation%20combining%20multiple%20treatment%20capabilities&width=400&height=250&seq=multi-functional&orientation=landscape"
    },
    {
      title: "Reservoir-Specific Treatments",
      description: "Tailored solutions based on specific reservoir characteristics and production profiles",
      applications: ["Carbonate reservoirs", "Sandstone formations", "Unconventional resources", "Heavy oil recovery"],
      image: "https://readdy.ai/api/search-image?query=Reservoir-specific%20chemical%20treatment%20with%20Golden%20Black%20technology%2C%20customized%20solutions%20for%20different%20geological%20formations&width=400&height=250&seq=reservoir-specific&orientation=landscape"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Customized Chemical Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            When standard solutions aren't enough, Golden Black develops bespoke chemical formulations 
            tailored to your specific operational requirements and challenges.
          </p>
        </div>

        {/* Customization Process */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Our Customization Process
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {customizationProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${process.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{process.step}</span>
                  </div>
                </div>
                
                <h4 className="text-lg font-bold text-gray-900 mb-3">{process.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Solution Types */}
        <div>
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Specialized Custom Solutions
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {customSolutionTypes.map((solution, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={solution.image}
                  alt={solution.title}
                  className="w-full h-48 object-cover"
                />
                
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h4>
                  <p className="text-gray-600 mb-4 leading-relaxed">{solution.description}</p>
                  
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Applications:</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {solution.applications.map((app, appIndex) => (
                        <div key={appIndex} className="flex items-center">
                          <i className="ri-check-line text-teal-600 mr-2 text-sm"></i>
                          <span className="text-gray-700 text-sm">{app}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Need a Custom Solution?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our R&D team is ready to develop tailored chemical solutions that meet your exact specifications. 
              Contact us to discuss your unique requirements.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/consultation" 
                className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Custom Development
              </a>
              <a 
                href="/contact" 
                className="border border-teal-600 text-teal-600 px-8 py-3 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Speak with R&D Team
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomSolutions;
